/* eslint-disable @typescript-eslint/no-dynamic-delete */
import { notify } from '@apia/notifications';
import {
  TPropsComparator,
  TPropsSelector,
  TPropsConfiguration,
  propsStore,
  usePropsSelector,
  getLabel,
} from '@apia/util';
import {
  TValidationField,
  TFieldValidationResult,
  TSuccessfulValidation,
  TValidationResult,
  TWrongValidation,
  TRegisterFormOptions,
  TUpdateFieldOptions,
  TFormState,
  TRegisterField,
  TRegisterFieldOptions,
} from './types';
import { focusOnField, hasSucceedFieldValidation, validateField } from './util';

export const validationsStore = new (class {
  activeForms: string[] = [];

  timeouts: Record<string, ReturnType<typeof setTimeout> | true> = {};

  loadFromSessionStorage(formName: string) {
    const storageValue = sessionStorage.getItem(this.makeFormName(formName));

    if (storageValue) {
      try {
        const parsedValue = JSON.parse(storageValue) as {
          formProps: Partial<TFormState>;
          fields: Record<string, Partial<TValidationField>>;
        };
        this.activeForms.push(formName);
        this.updateFormStore(formName, parsedValue.formProps, true);
        Object.entries(parsedValue.fields).forEach(([name, field]) => {
          propsStore.updateField(
            this.makeFieldName(formName, name),
            {
              ...this.getFieldStore(formName, name),
              ...field,
            },
            { noEmit: true },
          );
        });

        return true;
      } catch (e) {
        e;
      }
    }

    return false;
  }

  persistFormToSessionStorage(formName: string) {
    const { isTouched, isValid, validationResult, ...formProps } =
      this.getFormStore(formName);
    const shouldPersist = formProps.persistToSessionStorage;

    if (shouldPersist) {
      const fields = formProps.fields
        .map((current) => {
          const {
            validationFunction,
            isTouched,
            error,
            validationValueParser,
            ...field
          } = this.getFieldStore(formName, current);
          return field;
        })
        .reduce(
          (o, c) => ({ ...o, [c.name]: c }),
          {} as Record<string, Partial<TValidationField>>,
        );

      sessionStorage.setItem(
        this.makeFormName(formName),
        JSON.stringify({
          formProps,
          fields,
        }),
      );
    }
  }

  getFieldStore(formName: string, fieldName: string) {
    return propsStore.getFieldProps<TValidationField>(
      this.makeFieldName(formName, fieldName),
    );
  }

  getFormStore(formName: string) {
    return propsStore.getFieldProps<TFormState>(this.makeFormName(formName));
  }

  updateFieldStore(
    formName: string,
    fieldName: string,
    props: Partial<TValidationField>,
    noEmit?: boolean,
  ) {
    propsStore.updateField(
      this.makeFieldName(formName, fieldName),
      {
        ...this.getFieldStore(formName, fieldName),
        ...props,
      },
      { noEmit },
    );
    this.persistFormToSessionStorage(formName);
  }

  updateFormStore(
    formName: string,
    newState: Partial<TFormState>,
    avoidPersist = false,
  ) {
    propsStore.updateField(this.makeFormName(formName), newState);
    if (!avoidPersist) this.persistFormToSessionStorage(formName);
  }

  enableFormTouch(formName: string) {
    this.timeouts[formName] = true;
  }

  existsField = (formName: string, fieldName: string) => {
    return !!this.getFormStore(formName)?.fields?.includes(fieldName);
  };

  makeFieldName(formName: string, fieldName: string) {
    return `validation_${formName}_${fieldName}`;
  }

  makeFormName(formName: string) {
    return `validation_${formName}`;
  }

  registerField = (
    formName: string,
    registerData: TRegisterField,
    options: TRegisterFieldOptions = { registerIfExists: true },
  ) => {
    const formProps = this.getFormStore(formName);
    if (formProps === undefined) this.registerForm(formName);

    if (!this.getFormStore(formName)?.fields.includes(registerData.name))
      this.updateFormStore(formName, {
        fields: [
          ...(this.getFormStore(formName)?.fields ?? []),
          registerData.name,
        ],
      });

    const existsInStore = this.getFieldStore(formName, registerData.name);

    if (
      !existsInStore ||
      options.registerIfExists ||
      (!formProps?.avoidFieldsOverride &&
        existsInStore &&
        options.registerIfExists !== false)
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unused-vars
      /* 
      Se comenta debido a que al registrarse un campo, si este tiene valor inicial no se le asigna como valor real.
      En useField.ts, si registra el campo con value: fieldDefinition.initialValue. Al retirar value de la data de update,
      el value del campo queda undefined.
      */
      // const { value, ...updateData } = registerData;
      this.updateField(formName, registerData, {
        markFormAsTouched: false,
        avoidPropagation: options.avoidPropagation,
      });
      return;
    }
  };

  /**
   * Inicializa el estado de un nuevo formulario
   */
  registerForm = (
    formName: string,
    props: TRegisterFormOptions = { avoidFieldsOverride: false },
  ) => {
    props.persistToSessionStorage && this.loadFromSessionStorage(formName);

    if (this.activeForms.includes(formName)) {
      this.updateFormStore(
        formName,
        {
          avoidFieldsOverride: props.avoidFieldsOverride,
        },
        true,
      );
      if (props.initialValues)
        Object.entries(props.initialValues).forEach(([fieldId, fieldProps]) => {
          if (fieldProps instanceof Object)
            this.updateField(
              formName,
              { name: fieldId, ...fieldProps },
              { avoidFieldsOverride: props.avoidFieldsOverride },
            );
        });
      return;
    }
    this.activeForms.push(formName);
    this.updateFormStore(formName, {
      avoidFieldsOverride: props.avoidFieldsOverride,
      fields: [],
      isTouched: false,
      isValid: false,
      name: formName,
      validationResult: null,
      persistToSessionStorage: props.persistToSessionStorage,
    });

    // Set initial values
    Object.entries(props.initialValues ?? {}).forEach(([name, value]) => {
      this.updateField(
        formName,
        { name, value },
        {
          markFormAsTouched: false,
          avoidFieldsOverride: props.avoidFieldsOverride,
        },
      );
    });
  };

  /**
   * Setea el valor del campo con el nombre provisto. Si el campo no existe
   * en el formulario lo crea.
   */
  setFieldValue = (
    formName: string,
    fieldName: string,
    newValue: unknown,
    options?: TUpdateFieldOptions,
  ) => {
    if (!this.getFieldStore(formName, fieldName))
      this.registerField(formName, { name: fieldName, value: newValue });
    else
      this.updateField(
        formName,
        {
          name: fieldName,
          value: newValue,
          error: null,
        },
        options,
      );

    if (this.getFormStore(formName).isValid)
      this.updateFormStore(formName, { isValid: false });
  };

  setFormIsTouched = (formName: string, isTouched = true) => {
    this.updateFormStore(formName, { isTouched }, true);
  };

  /**
   * Elimina un campo del registro de validación de un formulario. De esta
   * forma cualquier validación que pudiera aplicarse sobre el mismo, ya no se
   * aplicará.
   */
  unregisterField = (formName: string, fieldName: string) => {
    if (this.getFormStore(formName))
      this.updateFormStore(formName, {
        fields: this.getFormStore(formName).fields.filter((current) => {
          return current !== fieldName;
        }),
      });
    propsStore.removeField(this.makeFieldName(formName, fieldName));
  };

  /**
   * Elimina un formulario del store de validación junto con todos los campos
   * asociados al mismo.
   */
  unregisterForm = (formName: string) => {
    this.getFormStore(formName)?.fields?.forEach((current) => {
      return propsStore.removeField(this.makeFieldName(formName, current));
    });
    this.activeForms = this.activeForms.filter((current) => {
      return current !== formName;
    });
    propsStore.removeField(this.makeFormName(formName));
    delete this.timeouts[formName];
  };

  /**
   * Actualiza las propiedades de un campo, si no existe, lo registra en el
   * formulario.
   */
  updateField = (
    formName: string,
    field: Partial<TValidationField> & Pick<TValidationField, 'name'>,
    options?: TUpdateFieldOptions,
  ) => {
    const previousValue = this.getFieldStore(formName, field.name)
      ?.value as unknown;

    if (this.getFormStore(formName))
      this.updateFormStore(formName, { isValid: false }, true);
    else this.registerForm(formName);

    if (previousValue !== undefined && options?.avoidFieldsOverride) return;

    const isTouched =
      field.value !== undefined &&
      field.value !== previousValue &&
      options?.markFormAsTouched !== false;

    if (!this.existsField(formName, field.name))
      this.registerField(formName, field, {
        avoidPropagation: options?.avoidPropagation,
      });
    this.updateFieldStore(
      formName,
      field.name,
      {
        ...field,
        isTouched:
          isTouched || this.getFieldStore(formName, field.name)?.isTouched,
      },
      options?.avoidPropagation,
    );
    if (isTouched) this.setFormIsTouched(formName);
  };

  /**
   * Actualiza múltiples campos al mismo tiempo.
   */
  updateMultipleFields = (
    formName: string,
    fields: (Partial<TValidationField> & Pick<TValidationField, 'name'>)[],
    options?: TUpdateFieldOptions,
  ) => {
    fields.forEach((current) => {
      return this.updateField(formName, current, options);
    });
  };

  /**
   * Ejecuta la validación sobre un formulario. En caso de que la validación
   * falle, se intenta hacer foco sobre el campo que no pudo validar.
   * Es importante notar que en caso de que un campo requiera validación,
   * debería estar presente en el DOM.
   *
   * No es necesario que el campo esté visible, ya que en caso de estar oculto
   * (por ejemplo por ser hijo de un acordeón), se emitirá un evento para
   * expandir al elemento padre que lo estuviera ocultando.
   *
   * @returns Devuelve una promesa que al ser resuelta otorga un objeto de tipo
   * TValidationResult. Este tipo está pensado para ser usado en conjunto con
   * el método hasSucceedFormValidation, que determinará si la validación fue
   * exitosa o hubo errores.
   *
   * @example
   *
   * const result = await validationStore.validateForm('myForm');
   * if(hasSucceedValidation(result)) {
   *   console.log('Success!', result);
   * } else {
   *   console.error('Validation error', result);
   * }
   */
  validateForm = async <
    ValueType extends Record<string, unknown>,
    SubmitValueType extends Record<keyof ValueType, unknown> = ValueType,
  >(
    formName: string,
  ) => {
    const state = this.getFormStore(formName);

    if (!state) return { errors: { form: 'No form with name ' + formName } };

    const fields = state.fields.map((current) => {
      return this.getFieldStore(formName, current);
    });

    const validationErrors: TWrongValidation = { errors: {} };
    const validationValues: TSuccessfulValidation<Record<string, unknown>> = {
      submitValues: {},
      values: {},
    };
    let hasFiredFocus = false;
    let existsErrors = false;
    const fieldsWithError: Record<string, TFieldValidationResult> = {};

    // eslint-disable-next-line no-restricted-syntax
    for (const field of fields.filter((current) => {
      return current !== undefined;
    })) {
      // eslint-disable-next-line no-await-in-loop
      const validationResult = await validateField(field, formName);
      fieldsWithError[field.name] = validationResult;

      if (hasSucceedFieldValidation(validationResult)) {
        validationValues.submitValues[field.submitName ?? field.name] =
          field.submitValueParser
            ? await field.submitValueParser(field.value)
            : field.value;
        validationValues.values[field.name] = field.value;
      } else {
        this.updateField(formName, {
          name: field.name,
          error: validationResult,
        });
        if (!hasFiredFocus) {
          // eslint-disable-next-line no-await-in-loop
          if (await focusOnField(formName, field)) hasFiredFocus = true;
        }
        existsErrors = true;
        validationErrors.errors[field.name] = validationResult;
      }
    }

    if (existsErrors) {
      if (!hasFiredFocus) {
        notify({
          message: getLabel('msgWrngVldNoFoc').text,
          type: 'danger',
        });
        console.error('Campos que fallaron la validación', fieldsWithError);
      }
      this.updateFormStore(
        formName,
        {
          isValid: false,
          validationResult: validationErrors,
        },
        true,
      );
      return validationErrors as TValidationResult<ValueType, SubmitValueType>;
    }
    this.updateFormStore(
      formName,
      {
        isValid: true,
        validationResult: validationValues,
      },
      true,
    );
    return validationValues as TValidationResult<ValueType, SubmitValueType>;
  };
})();

/**
 * Este hook permite suscribirse a los cambios realizados en el
 * store de validación de un formulario. Funciona igual a un
 * useAppSelector en cuanto a la función selectora y comparadora.
 *
 * @param selector
 * @param comparator
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useFormSelector<Selected = any>(
  formName: string,
  selector: TPropsSelector<Selected, TFormState>,
  comparator?: TPropsComparator<Selected>,
) {
  return usePropsSelector(
    validationsStore.makeFormName(formName),
    selector,
    comparator,
  );
}

/**
 * Este hook permite suscribirse a los cambios realizados en el
 * store de validación de un campo de formulario. Funciona igual a un
 * useAppSelector en cuanto a la función selectora y comparadora.
 *
 * @param selector
 * @param comparator
 * @returns
 */

export function useFieldSelector<
  ValueType = unknown,
  SubmitValueType = unknown,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Selected = any,
>(
  formName: string,
  fieldName: string,
  selector?: TPropsSelector<
    Selected,
    TValidationField<ValueType, SubmitValueType>
  >,
  comparator?: TPropsComparator<Selected>,
): Selected;
export function useFieldSelector<
  ValueType = unknown,
  SubmitValueType = unknown,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Selected = any,
>(
  formName: string,
  fieldName: string,
  configurator?: TPropsConfiguration<
    Selected,
    TValidationField<ValueType, SubmitValueType>
  >,
): Selected;
export function useFieldSelector<
  ValueType = unknown,
  SubmitValueType = unknown,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Selected = any,
>(
  formName: string,
  fieldName: string,
  confOrSelector?:
    | TPropsSelector<Selected, TValidationField<ValueType, SubmitValueType>>
    | TPropsConfiguration<
        Selected,
        TValidationField<ValueType, SubmitValueType>
      >,
  comparator?: TPropsComparator<Selected>,
): Selected {
  const fieldId = validationsStore.makeFieldName(formName, fieldName);
  return usePropsSelector(fieldId, confOrSelector, comparator);
}
